/**
 * Lunch grouping - day, evening or night.
 *
 * The historical schedules run two completely separate lunch lists: a daytime
 * one (12:00–16:00) and an evening one (18:00–20:00). Which list somebody lands
 * on is not a scoring preference, it is a hard classification made once per
 * shift, so it lives here rather than inside the solver. Getting it wrong is
 * very visible - a 14:00–22:00 starter offered a 12:30 lunch is nonsense before
 * any coverage maths happens.
 *
 * The classification is deliberately data-driven: `eveningGroupStartTime` and
 * `eveningGroupEndTime` are editable settings and any employee can be pinned
 * with `lunchGroupOverride`, so a manager can correct us without a code change.
 */

import { DEFAULT_SETTINGS, LUNCH_GROUPS } from '../core/settings.js';
import { MINUTES_PER_DAY, formatMinutes, parseTimeToMinutes } from '../core/time.js';

const VALID_GROUPS = new Set(Object.values(LUNCH_GROUPS));

/**
 * Night breaks have no home in a day profile - `resolveDayConfig` only carries a
 * day and an evening window - so this is the last-resort window used when
 * somebody is pinned to the night group without actually working an overnight
 * shift. It sits on the "past midnight" number line (01:00 becomes 1500) because
 * that is where overnight shift times live. `eligibility.js` replaces it with a
 * policy-specific window for real overnight shifts.
 */
const FALLBACK_NIGHT_WINDOW = Object.freeze({
  start: MINUTES_PER_DAY + (parseTimeToMinutes(DEFAULT_SETTINGS.nightWindowStart) ?? 60),
  end: MINUTES_PER_DAY + (parseTimeToMinutes(DEFAULT_SETTINGS.nightWindowEnd) ?? 240),
});

/**
 * Decide which lunch list a shift belongs to.
 *
 * `source` is returned alongside the group because the UI has to be able to say
 * *why* somebody is on the evening list - an automatic classification can be
 * argued with, a manager's explicit override cannot. `reason` is a finished
 * sentence for the same purpose; building it here keeps the cut-off numbers
 * beside the comparison that used them.
 *
 * Precedence: an explicit `lunchGroupOverride` wins, then an overnight shift,
 * then the evening cut-offs, then day.
 *
 * @param {object} shift ShiftRecord (or any object with startMinutes/endMinutes/isOvernight).
 * @param {object} settings Global settings; supplies the evening cut-offs.
 * @param {object} [rule] The employee's StaffRule, if they have one.
 * @returns {{ group: string, source: 'auto'|'override'|'overnight', reason: string }}
 */
export function classifyShiftGroup(shift, settings, rule) {
  const override = normaliseGroup(rule?.lunchGroupOverride);
  if (override) {
    return {
      group: override,
      source: 'override',
      reason: `Pinned to the ${override} lunch group by a staff rule, whatever the shift times say.`,
    };
  }

  const startMinutes = Number.isFinite(shift?.startMinutes) ? shift.startMinutes : null;
  const endMinutes = Number.isFinite(shift?.endMinutes) ? shift.endMinutes : null;

  // `isOvernight` is decided once, in model.js, against
  // `overnightEndThresholdMinutes` - a 16:00–00:00 shift finishes *at* midnight
  // and is an ordinary evening shift, so we must never re-derive it from
  // `crossesMidnight` here.
  if (shift?.isOvernight === true) {
    return {
      group: LUNCH_GROUPS.NIGHT,
      source: 'overnight',
      reason: endMinutes === null
        ? 'The shift runs past midnight, so it takes a night break rather than a day or evening lunch.'
        : `The shift runs past midnight and finishes at ${formatMinutes(endMinutes)} the next day, `
          + 'so it takes a night break rather than a day or evening lunch.',
    };
  }

  if (startMinutes === null || endMinutes === null) {
    return {
      group: LUNCH_GROUPS.DAY,
      source: 'auto',
      reason: 'No readable shift times, so the day group is assumed.',
    };
  }

  const eveningStart = cutoff(settings?.eveningGroupStartTime, DEFAULT_SETTINGS.eveningGroupStartTime, 840);
  const eveningEnd = cutoff(settings?.eveningGroupEndTime, DEFAULT_SETTINGS.eveningGroupEndTime, 1320);

  if (startMinutes >= eveningStart) {
    return {
      group: LUNCH_GROUPS.EVENING,
      source: 'auto',
      reason: `Starts at ${formatMinutes(startMinutes)}, at or after the `
        + `${formatMinutes(eveningStart)} evening cut-off.`,
    };
  }

  if (endMinutes >= eveningEnd) {
    return {
      group: LUNCH_GROUPS.EVENING,
      source: 'auto',
      reason: `Finishes at ${formatMinutes(endMinutes)}, at or after the `
        + `${formatMinutes(eveningEnd)} evening cut-off.`,
    };
  }

  return {
    group: LUNCH_GROUPS.DAY,
    source: 'auto',
    reason: `Runs ${formatMinutes(startMinutes)}-${formatMinutes(endMinutes)}, entirely inside the `
      + `daytime cut-offs (starts before ${formatMinutes(eveningStart)}, finishes before `
      + `${formatMinutes(eveningEnd)}).`,
  };
}

/**
 * The search window for a group on one day.
 *
 * Returned as a fresh object every time: callers store it on an eligibility
 * result and the day config must never be mutable through it.
 *
 * @param {string} group 'day' | 'evening' | 'night'.
 * @param {object} dayConfig Output of `resolveDayConfig` - already in minutes.
 * @returns {{ start: number, end: number }}
 */
export function windowForGroup(group, dayConfig) {
  if (group === LUNCH_GROUPS.EVENING) {
    return {
      start: numberOr(dayConfig?.eveningWindow?.start, 1080),
      end: numberOr(dayConfig?.eveningWindow?.end, 1200),
    };
  }

  if (group === LUNCH_GROUPS.NIGHT) {
    return { ...FALLBACK_NIGHT_WINDOW };
  }

  return {
    start: numberOr(dayConfig?.dayWindow?.start, 720),
    end: numberOr(dayConfig?.dayWindow?.end, 960),
  };
}

/** Accept a group override only when it names a real group, so typos fall back to auto. */
function normaliseGroup(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return VALID_GROUPS.has(trimmed) ? trimmed : null;
}

/** Resolve a "14:00"-style cut-off through the default, then a hard floor. */
function cutoff(value, fallbackText, fallbackMinutes) {
  return parseTimeToMinutes(value) ?? parseTimeToMinutes(fallbackText) ?? fallbackMinutes;
}

function numberOr(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}
